import {vec3, vec4} from "_src/libs/gl-matrix";

const _vec3Tmp = vec3.create();

export default {

  zero(zeroed) {
    zeroed[0] = 0;
    zeroed[1] = 0;
    zeroed[2] = 0;
    zeroed[3] = 0;
    zeroed[4] = 0;
    zeroed[5] = 0;

    return zeroed;
  },

  reset(reseted) {

    reseted[0] = Infinity;
    reseted[1] = Infinity;
    reseted[2] = Infinity;
    reseted[3] = -Infinity;
    reseted[4] = -Infinity;
    reseted[5] = -Infinity;

    return reseted;

  },

  copy(dest, minmax) {

    dest[0] = minmax[0];
    dest[1] = minmax[1];
    dest[2] = minmax[2];
    dest[3] = minmax[3];
    dest[4] = minmax[4];
    dest[5] = minmax[5];

    return dest;

  },

  expandMinmax(expanded, expanding) {

    expanded[0] = Math.min(expanded[0], expanding[0]);
    expanded[1] = Math.min(expanded[1], expanding[1]);
    expanded[2] = Math.min(expanded[2], expanding[2]);
    expanded[3] = Math.max(expanded[3], expanding[3]);
    expanded[4] = Math.max(expanded[4], expanding[4]);
    expanded[5] = Math.max(expanded[5], expanding[5]);

    return expanded;

  },

  expandPoint(expanded, point) {

    expanded[0] = Math.min(expanded[0], point[0]);
    expanded[1] = Math.min(expanded[1], point[1]);
    expanded[2] = Math.min(expanded[2], point[2]);
    expanded[3] = Math.max(expanded[3], point[0]);
    expanded[4] = Math.max(expanded[4], point[1]);
    expanded[5] = Math.max(expanded[5], point[2]);

    return expanded;

  },

  expandMinmaxByScene(minmax, scene) {
    for (const child of scene.getChildren()) {
      if (child.isDrawable && !child.checkIsHidden()) {
        this.expandMinmax(minmax, child.minmaxWorld);
      }
    }
  },

  calcNearFarByMinMax: (function() {

    const vec3Tmp = vec3.create();
    const vec4Tmp = vec4.create();

    return function(minmax, camera) {

      const nearPlane = vec3.set(vec4Tmp, 0, 0, -1);
      vec3.transformQuat(nearPlane, nearPlane, camera.quat);
      vec4Tmp[3] = -vec3.dot(nearPlane, camera.trans);

      const vertex = vec3Tmp;
      let near = Infinity;
      let far = -Infinity;

      for (let i = 0; i < 8; ++i) {

        vertex[0] = minmax[    3 * ((i & 0b100) >> 2)];
        vertex[1] = minmax[1 + 3 * ((i & 0b010) >> 1)];
        vertex[2] = minmax[2 + 3 * ((i & 0b001))];

        const dist = vec3.dot(nearPlane, vertex) + vec4Tmp[3];

        near = Math.min(dist, near);
        far = Math.max(dist, far);

      }

      if (camera.isPerspective()) {
        const minPerspectiveNear = far > 1000 ? 0.1 : 0.1 * Math.sqrt(Math.abs(far) / 1000);
        near = Math.max(near, minPerspectiveNear);
      }
      if (far <= near) far = near + 0.001;

      return [near, far];
    };

  })(),

  isValid(minmax) {
    return minmax[0] <= minmax[3] && minmax[1] <= minmax[4] && minmax[2] <= minmax[5];
  },

  getDefaultValue() {
    return [-10, -10, -10, 10, 10, 10];
  },

  setDefaultValue(dest) {
    dest[0] = -10;
    dest[1] = -10;
    dest[2] = -10;
    dest[3] = 10;
    dest[4] = 10;
    dest[5] = 10;

    return dest;
  },

  setMinmaxBuffer(chunk, minmax) {

    const geometry = chunk.geometry;

    const positions = geometry.getItemBuffer("a_position");
    const positionsNext = geometry.getItemBuffer("a_position_next");

    const xmin = minmax[0];
    const ymin = minmax[1];
    const zmin = minmax[2];
    const xmax = minmax[3];
    const ymax = minmax[4];
    const zmax = minmax[5];

    setLine(positions, positionsNext, 0, xmax, ymin, zmax, xmax, ymin, zmin);
    setLine(positions, positionsNext, 1, xmax, ymin, zmin, xmin, ymin, zmin);
    setLine(positions, positionsNext, 2, xmin, ymin, zmin, xmin, ymin, zmax);
    setLine(positions, positionsNext, 3, xmin, ymin, zmax, xmax, ymin, zmax);

    setLine(positions, positionsNext, 4, xmax, ymax, zmax, xmax, ymax, zmin);
    setLine(positions, positionsNext, 5, xmax, ymax, zmin, xmin, ymax, zmin);
    setLine(positions, positionsNext, 6, xmin, ymax, zmin, xmin, ymax, zmax);
    setLine(positions, positionsNext, 7, xmin, ymax, zmax, xmax, ymax, zmax);

    setLine(positions, positionsNext, 8, xmax, ymin, zmax, xmax, ymax, zmax);
    setLine(positions, positionsNext, 9, xmax, ymin, zmin, xmax, ymax, zmin);
    setLine(positions, positionsNext, 10, xmin, ymin, zmin, xmin, ymax, zmin);
    setLine(positions, positionsNext, 11, xmin, ymin, zmax, xmin, ymax, zmax);

    chunk.markAttributeDirty("a_position");
    chunk.markAttributeDirty("a_position_next");

  },

  recomputedFromTransform(dest, minmax, transform) {

    let xmin = Infinity;
    let ymin = Infinity;
    let zmin = Infinity;
    let xmax = -Infinity;
    let ymax = -Infinity;
    let zmax = -Infinity;

    for (let i = 0; i < 8; ++i) {

      const x = minmax[    3 * ((i & 0b100) >> 2)];
      const y = minmax[1 + 3 * ((i & 0b010) >> 1)];
      const z = minmax[2 + 3 * ((i & 0b001))];

      const point = vec3.set(_vec3Tmp, x, y, z);

      vec3.transformMat4(point, point, transform);

      xmin = Math.min(xmin, point[0]);
      ymin = Math.min(ymin, point[1]);
      zmin = Math.min(zmin, point[2]);
      xmax = Math.max(xmax, point[0]);
      ymax = Math.max(ymax, point[1]);
      zmax = Math.max(zmax, point[2]);

    }

    dest[0] = xmin;
    dest[1] = ymin;
    dest[2] = zmin;
    dest[3] = xmax;
    dest[4] = ymax;
    dest[5] = zmax;

    return dest;
  },

  recomputedFromRotated(dest, minmax, matrix) {

    for (let i = 0; i < 3; ++i) {

      dest[i] = matrix[12 + i];
      dest[3 + i] = matrix[12 + i];

      for (let j = 0; j < 3; ++j) {

        const offset = j * 4;

        const e = matrix[i + offset] * minmax[j];
        const f = matrix[i + offset] * minmax[3 + j];

        if (e < f) {

          dest[i] += e;
          dest[3 + i] += f;

        } else {

          dest[i] += f;
          dest[3 + i] += e;

        }

      }

    }

    return dest;

  },

  getDiagonal(minmax) {

    const diffx = minmax[3] - minmax[0];
    const diffy = minmax[4] - minmax[1];
    const diffz = minmax[5] - minmax[2];

    return Math.sqrt(diffx * diffx + diffy * diffy + diffz * diffz);

  }

}

function setLine(positions, positionsNext, index, x0, y0, z0, x1, y1, z1) {

  let offset = 4 * 3 * index;

  // 0
  positions[offset + 0] = x0;
  positions[offset + 1] = y0;
  positions[offset + 2] = z0;

  positionsNext[offset + 0] = x1;
  positionsNext[offset + 1] = y1;
  positionsNext[offset + 2] = z1;

  offset += 3;
  // 1
  positions[offset + 0] = x1;
  positions[offset + 1] = y1;
  positions[offset + 2] = z1;

  positionsNext[offset + 0] = x0;
  positionsNext[offset + 1] = y0;
  positionsNext[offset + 2] = z0;

  offset += 3;
  // 2
  positions[offset + 0] = x0;
  positions[offset + 1] = y0;
  positions[offset + 2] = z0;

  positionsNext[offset + 0] = x1;
  positionsNext[offset + 1] = y1;
  positionsNext[offset + 2] = z1;

  offset += 3;
  // 3
  positions[offset + 0] = x1;
  positions[offset + 1] = y1;
  positions[offset + 2] = z1;

  positionsNext[offset + 0] = x0;
  positionsNext[offset + 1] = y0;
  positionsNext[offset + 2] = z0;

}
